<template>
  <v-container fluid class="pa-0">
    <!-- KPIs Dashboard -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card
          class="mx-1 kpi-card"
          elevation="2"
          :style="{ borderTop: '4px solid #4CAF50' }"
        >
          <v-card-text class="text-center py-4">
            <div class="text-overline mb-1">Vendedores Activos</div>
            <div class="text-h5 font-weight-bold mb-2">
              {{ kpiData.total_activos || 0 }}
            </div>
            <div class="text-caption grey--text">
              {{ Math.round((kpiData.total_activos / kpiData.total) * 100) || 0 }}% del
              total
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card
          class="mx-1 kpi-card"
          elevation="2"
          :style="{ borderTop: '4px solid #FF5252' }"
        >
          <v-card-text class="text-center py-4">
            <div class="text-overline mb-1">Vendedores Inactivos</div>
            <div class="text-h5 font-weight-bold mb-2">
              {{ kpiData.total_inactivos || 0 }}
            </div>
            <div class="text-caption grey--text">
              {{ Math.round((kpiData.total_inactivos / kpiData.total) * 100) || 0 }}% del
              total
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card
          class="mx-1 kpi-card"
          elevation="2"
          :style="{ borderTop: '4px solid #2196F3' }"
        >
          <v-card-text class="text-center py-4">
            <div class="text-overline mb-1">Pendientes</div>
            <div class="text-h5 font-weight-bold mb-2">
              {{ kpiData.total_pendientes || 0 }}
            </div>
            <div class="text-caption grey--text">
              {{ Math.round((kpiData.total_pendientes / kpiData.total) * 100) || 0 }}% del
              total
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card
          class="mx-1 kpi-card"
          elevation="2"
          :style="{ borderTop: '4px solid #FFC107' }"
        >
          <v-card-text class="text-center py-4">
            <div class="text-overline mb-1">Total Vendedores</div>
            <div class="text-h5 font-weight-bold mb-2">
              {{ kpiData.total || 0 }}
            </div>
            <div class="text-caption grey--text">
              Último actualizado: {{ formattedCurrentDate }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card flat class="mx-auto" max-width="1200">
      <v-app-bar flat dense color="secondary" dark>
        <v-toolbar-title>Vendors</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="warning" @click="openDialog" elevation="2">
          <v-icon left>mdi-plus</v-icon>
          Add Vendor
        </v-btn>
      </v-app-bar>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="vendors"
          :loading="loading"
          :search="search"
          class="elevation-0"
        >
          <template v-slot:top>
            <v-text-field
              v-model="search"
              label="Search vendors"
              prepend-inner-icon="mdi-magnify"
              class="mx-4 mt-4"
              outlined
              dense
              hide-details
            ></v-text-field>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="editItem(item)" class="mr-2">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small @click="showVendorStats(item)" color="info">
              <v-icon small>mdi-chart-bar</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.estado="{ item }">
            <v-chip :color="getStatusColor(item.estado)" small label text-color="white">
              {{ item.estado }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="700px">
      <v-card>
        <v-card-title
          class="headline grey lighten-2 px-4 py-3 d-flex justify-space-between"
        >
          {{ formTitle }}
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedItem.codigo"
                    :rules="[(v) => !!v || 'Code is required']"
                    hide-details
                    label="Code"
                    required
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedItem.cedula"
                    :rules="[(v) => !!v || 'ID is required']"
                    label="ID"
                    hide-details
                    required
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="12">
                  <v-text-field
                    v-model="editedItem.nombre"
                    :rules="[(v) => !!v || 'Name is required']"
                    label="Name"
                    hide-details
                    required
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editedItem.celular"
                    :rules="[(v) => !v || /^[0-9]+$/.test(v) || 'Phone must be valid']"
                    label="Phone"
                    outlined
                    hide-details
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="editedItem.email"
                    :rules="[(v) => !v || /.+@.+\..+/.test(v) || 'E-mail must be valid']"
                    label="Email"
                    hide-details
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                    v-model="editedItem.estado"
                    :items="statusOptions"
                    :rules="[(v) => !!v || 'Status is required']"
                    label="Status"
                    hide-details
                    required
                    outlined
                    dense
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close"> Cancel </v-btn>
          <v-btn color="blue darken-1" :disabled="!valid" :loading="saving" @click="save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vendor Stats Dialog -->
    <v-dialog v-model="statsDialog" scrollable>
      <v-card>
        <v-card-title
          class="headline grey lighten-2 px-4 py-3 d-flex justify-space-between"
        >
          <span>Sales Statistics: {{ selectedVendor.nombre }}</span>
          <v-btn icon @click="statsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-row class="mb-3">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="statsDateFrom"
                label="From Date"
                hide-details="auto"
                outlined
                dense
                type="date"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="statsDateTo"
                label="To Date"
                hide-details="auto"
                outlined
                dense
                type="date"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-btn color="primary" @click="getVendorStats" :loading="loadingStats">
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>

          <v-row v-if="vendorStats.length > 0">
            <v-col cols="12">
              <v-card flat>
                <v-card-text>
                  <v-row class="text-center">
                    <v-col cols="4">
                      <div class="text-overline">Total Sales</div>
                      <div class="text-h5 font-weight-bold">
                        {{ formatCurrency(totalSales) }}
                      </div>
                    </v-col>
                    <v-col cols="4">
                      <div class="text-overline">Total Quantity</div>
                      <div class="text-h5 font-weight-bold">{{ totalQuantity }}</div>
                    </v-col>
                    <v-col cols="4">
                      <div class="text-overline">Total Discount</div>
                      <div class="text-h5 font-weight-bold">
                        {{ formatCurrency(totalDiscount) }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div v-if="loadingStats" class="text-center my-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="grey--text mt-2">Loading vendor statistics...</div>
          </div>

          <div v-else-if="vendorStats.length === 0" class="text-center my-6 pa-4">
            <v-icon large color="grey lighten-1">mdi-chart-box-outline</v-icon>
            <div class="grey--text mt-2">No sales data available for this period</div>
          </div>

          <v-data-table
            v-else
            :headers="statsHeaders"
            :items="vendorStats"
            :items-per-page="5"
            class="mt-4"
          >
            <template v-slot:item.valor="{ item }">
              <span>{{ formatCurrency(item.valor) }}</span>
            </template>
            <template v-slot:item.descuento="{ item }">
              <span>{{ formatCurrency(item.descuento) }}</span>
            </template>
          </v-data-table>

          <div v-if="vendorStats.length > 0">
            <GChart
              type="ColumnChart"
              :data="chartData"
              :options="chartOptions"
              class="mt-4"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday, getdays_ago } from "../services/webserver.js";
import { GChart } from "vue-google-charts";

export default {
  components: {
    GChart,
  },
  data: () => ({
    dialog: false,
    statsDialog: false,
    loading: false,
    loadingStats: false,
    saving: false,
    search: "",
    valid: true,
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",
    headers: [
      { text: "Store", value: "store" },
      { text: "Code", value: "codigo" },
      { text: "Name", value: "nombre" },
      { text: "Status", value: "estado" },
      { text: "Email", value: "email" },
      { text: "Phone", value: "celular" },
      { text: "Actions", value: "actions", sortable: false, align: "end" },
    ],
    statsHeaders: [
      { text: "Code", value: "codigo", align: "start" },
      { text: "Discount", value: "descuento", align: "center" },
      { text: "Value", value: "valor", align: "end" },
      { text: "Quantity", value: "quantity", align: "end" },
      { text: "Days", value: "days", align: "end" },
      { text: "Average per Day", value: "promedio", align: "end" },
    ],
    statusOptions: ["Activo", "Inactivo", "Pendiente"],
    vendors: [],
    editedIndex: -1,
    editedItem: {
      store: "",
      codigo: "",
      cedula: "",
      nombre: "",
      estado: "",
      email: "",
      celular: "",
    },
    defaultItem: {
      store: "",
      codigo: "",
      cedula: "",
      nombre: "",
      estado: "Activo",
      email: "",
      celular: "",
    },
    // KPI Data
    kpiData: {
      total: 0,
      total_activos: 0,
      total_inactivos: 0,
      total_pendientes: 0,
    },
    // Vendor stats
    statsDateFrom: getdays_ago(-30),
    statsDateTo: getToday(),
    selectedVendor: {},
    vendorStats: [],
    chartData: [],
    chartOptions: {
      curveType: "function",
      legend: { position: "right" },
      interpolateNulls: true,
      crosshair: { trigger: "both", opacity: 0.95, color: "#39FF14" },
      title: "Vendor Sales Distribution",
      isStacked: false,
      hAxis: {
        title: "",
        slantedText: true,
        slantedTextAngle: 15,
      },
      colors: ["#1b9e77", "#d95f02", "#7570b3"],
      height: 400,
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Vendor" : "Edit Vendor";
    },
    formattedCurrentDate() {
      const date = new Date();
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
    totalSales() {
      return this.vendorStats.reduce((sum, item) => sum + parseFloat(item.valor || 0), 0);
    },
    totalQuantity() {
      return this.vendorStats.reduce(
        (sum, item) => sum + parseFloat(item.quantity || 0),
        0
      );
    },
    totalDiscount() {
      return this.vendorStats.reduce(
        (sum, item) => sum + parseFloat(item.descuento || 0),
        0
      );
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  created() {
    this.get_vendors();
  },

  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    async get_vendors() {
      var qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.store.store_id,
          },
        ],
      };
      this.loading = true;
      let promise = new Promise((resolve, reject) => {
        webserver(
          "vendors",
          qry,
          function (data) {
            resolve(data);
          },
          function () {
            reject([]);
          }
        );
      });

      var result = await promise;
      this.loading = false;
      if (result.length > 0) {
        this.vendors = result;
        this.updateKpiData();
      } else {
        this.vendors = [];
      }
    },

    updateKpiData() {
      // Reset KPI data
      const kpis = {
        total: this.vendors.length,
        total_activos: 0,
        total_inactivos: 0,
        total_pendientes: 0,
      };

      // Count vendors by status
      this.vendors.forEach((vendor) => {
        if (vendor.estado === "Activo") {
          kpis.total_activos++;
        } else if (vendor.estado === "Inactivo") {
          kpis.total_inactivos++;
        } else if (vendor.estado === "Pendiente") {
          kpis.total_pendientes++;
        }
      });

      this.kpiData = kpis;
    },

    editItem(item) {
      this.editedIndex = this.vendors.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    openDialog() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    async save() {
      delete this.editedItem.qty;
      this.editedItem.store = window.store.store_id;
      this.saving = true;
      var qry = {
        store: window.store.store_id,
        data: [],
      };
      qry.data.push(this.editedItem);
      console.log("sAVE", qry);
      let promise = new Promise((resolve, reject) => {
        webserver(
          "vendors_update",
          qry,
          function (data) {
            resolve(data);
          },
          function () {
            reject([]);
          }
        );
      });

      var result = await promise;
      this.saving = false;
      if (result.length > 0) {
        this.showSnackbar("Vendor saved successfully", "success");
        this.dialog = false;
      } else {
        this.showSnackbar("Error saving vendor", "error");
      }
      this.get_vendors();
    },

    getStatusColor(status) {
      if (status === "Activo") return "green";
      if (status === "Inactivo") return "grey";
      return "orange"; // Para 'Pending' u otros estados
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    // Vendor stats methods
    showVendorStats(vendor) {
      this.selectedVendor = vendor;
      this.statsDialog = true;
      this.getVendorStats();
    },

    async getVendorStats() {
      this.loadingStats = true;
      this.vendorStats = [];

      var qry = {
        store: window.store.store_id,
        dateFrom: this.statsDateFrom,
        dateTo: this.statsDateTo,
        vendorId: this.selectedVendor.codigo,
      };

      webserver("vendors_stat", qry, (data) => {
        this.vendorStats = data;
        this.loadingStats = false;
        this.prepareChartData();
      });
    },

    prepareChartData() {
      if (!this.vendorStats.length) {
        this.chartData = [];
        return;
      }

      // Prepare chart data
      const chartData = [["Month", "Sales Value", "Quantity", "Discount"]];

      // Group by month if data spans multiple months
      const monthlyData = {};

      this.vendorStats.forEach((item) => {
        // Assuming there's a date field in the stats or using current date ranges
        const date = new Date(item.fecha || this.statsDateFrom);
        const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = {
            valor: 0,
            quantity: 0,
            descuento: 0,
          };
        }

        monthlyData[monthYear].valor += parseFloat(item.valor || 0);
        monthlyData[monthYear].quantity += parseFloat(item.quantity || 0);
        monthlyData[monthYear].descuento += parseFloat(item.descuento || 0);
      });

      // Convert the grouped data to chart format
      Object.entries(monthlyData).forEach(([month, data]) => {
        chartData.push([month, data.valor, data.quantity, data.descuento]);
      });

      this.chartData = chartData;
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep table {
  border-collapse: separate;
  border-spacing: 0 8px;
}

.v-data-table ::v-deep tbody tr {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s ease-in-out;
}

.v-data-table ::v-deep tbody tr:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.v-data-table ::v-deep td {
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

.v-data-table ::v-deep td:first-child {
  border-left: 1px solid #f0f0f0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.v-data-table ::v-deep td:last-child {
  border-right: 1px solid #f0f0f0;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* KPI card styles */
.kpi-card {
  transition: transform 0.3s;
}

.kpi-card:hover {
  transform: translateY(-5px);
}
</style>
